import { EnvConfiguration } from '../config/EnvConfiguration';
import { MediaTokenProvider } from './MediaTokenProvider';
import { buildAuthorizedMediaUrl } from './buildAuthorizedMediaUrl';
import { UploadsAwaitingLinks } from './UploadsAwaitingLinks';

// Turns a stored media url into the url to actually load. Attachments and member photos both sit
// on the public uploads bucket and both now have an authorised, audited route in front of them;
// this is the one place that decides which to use.
//
// The credential travels as a query parameter because most consumers load the url directly (an
// <img>, a CSS background-image, a new tab), and those cannot set headers. AuthMiddleware accepts it
// there for exactly that reason. It is deliberately a media-scoped token rather than the session's
// access token: a url reaches browser history, referrer headers, access logs and session replay
// (which masks text only, so it records image urls in full), and this token reaches only the media
// routes, for whatever media the principal could already read. It is not scoped to the single
// image it was minted for, so it is the short expiry rather than the scope that limits a leaked one.
export class AuthorizedMediaUrlResolver {

    static inject = () => [MediaTokenProvider, EnvConfiguration, UploadsAwaitingLinks];
    constructor(
        private mediaTokenProvider: MediaTokenProvider,
        private config: EnvConfiguration,
        private uploadsAwaitingLinks: UploadsAwaitingLinks
    ) {}

    // What to load when the server offered no link: an object we do not host (a social login's
    // account picture), anything served by a deploy older than the route, or a file uploaded here
    // and not saved yet, which loads from the copy this device still holds. A hosted photo that
    // arrives without its link is HostedPhotoGuard's to refuse before reaching here. Synchronous so
    // a render can use it without waiting.
    unlinkedUrl = (url: string): string => this.uploadsAwaitingLinks.urlFor(url);

    // THROWS when a link was offered and no credential could be obtained. It used to fall back to
    // the stored bucket url, which silently turned every failed mint into an unaudited read of a
    // public object, the exact thing the download route exists to stop.
    //
    // Callers must therefore handle a rejection. Doing nothing with it is worse than the old
    // fallback: an uncaught one in a render takes down the nearest error boundary, and in a click
    // handler nobody awaits it becomes an unhandled rejection that tells nobody. useResolvedMedia,
    // AttachmentOpener and CareCodeDocumentBuilder each catch, report, and degrade.
    resolve = async (params: { url: string; href?: string }): Promise<string> => {
        const { url, href } = params;

        // No link costs no token fetch. The server offers one only where it can actually serve the
        // object, so the absence of a link is a decision it has already made rather than something
        // to second-guess here.
        if (!href) {
            return this.unlinkedUrl(url);
        }

        const token = await this.mediaTokenProvider.get(href);

        return buildAuthorizedMediaUrl({ href, token, apiBaseUrl: this.config.API_BASE_URL });
    };
}
